import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { AuctionForm } from "../components/exchange/auction";
import auctionService from "../services/auctionService";
import type { AuctionFormValues } from "../components/exchange/auction/AuctionForm";
import { Item } from "../types/Item";

const EditAuctionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchItem = async () => {
      if (!id) {
        setError("No auction ID provided");
        setLoading(false);
        return;
      }
      try {
        const response = await auctionService.getItem(id);
        if (!response) {
          throw new Error("Auction not found.");
        }
        setItem(response);
      } catch (err: any) {
        setError(err?.message || "Failed to load auction.");
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  const handleSubmit = async (values: AuctionFormValues) => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("starting_price", values.starting_price.toString());
      formData.append("min_increment", values.min_increment.toString());
      formData.append("end_time", values.end_time?.toISOString() || "");
      formData.append("category", values.category);
      formData.append("quantity", String(values.quantity || 1));
      formData.append("images", JSON.stringify(values.images));
      if (values.video) {
        formData.append("video", values.video);
      }
      await auctionService.updateItem(id, formData);
      setSuccess("Auction updated successfully!");
      setTimeout(() => navigate("/my-auctions"), 2000);
    } catch (err) {
      console.error("Error updating auction:", err);
      setError("Failed to update auction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (!item) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Auction not found</Alert>
      </Box>
    );
  }
  const auction = (item as any).item ? (item as any).item : item;
  const initialValues: AuctionFormValues = {
    title: auction.title || "",
    description: auction.description || "",
    starting_price: auction.starting_price ?? 0,
    min_increment: auction.min_increment ?? 0,
    end_time: auction.end_time ? new Date(auction.end_time) : null,
    category: auction.category || "",
    quantity: auction.quantity ?? 1,
    images: Array.isArray(auction.images) ? auction.images : [],
    video: auction.video || undefined,
    parcel_id: auction.parcel_id || undefined,
  };
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Edit Auction
      </Typography>
      <AuctionForm
        key={item.id}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isEdit={true}
        loading={loading}
        error={error}
        success={success}
      />
    </Box>
  );
};

export default EditAuctionPage;
